import { notFound } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetDocumentDetailPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
  documentId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the document detail page.
 *
 * Fetches document detail and business detail in a single HTTP round trip
 * on client-side navigation.
 */
export const getDocumentDetailPageData = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetDocumentDetailPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documentDetail.pageData", async () => {
      const [businessMod, documentMod] = await Promise.all([
        import("../business/business.server"),
        import("./document.server"),
      ]);
      const { getBusinessDetailForUser } = businessMod;
      const { getDocumentForUser } = documentMod;

      const [businessDetail, document] = await Promise.all([
        getBusinessDetailForUser(context.user.id, data.businessId),
        getDocumentForUser(context.user.id, data.businessId, data.documentId),
      ]);

      if (!document) {
        // notFound() (not a bare Response) — thrown Responses can't be
        // serialized by the server RPC layer (Seroval crash → 500) and aren't
        // rejected properly on the client. notFound() renders the route's
        // notFoundComponent with a clean 404.
        throw notFound();
      }

      return { businessDetail, document } as const;
    });
  });
